import React, { useMemo } from "react";
import classes from "./index.module.scss";
import { message, Select, Table } from "antd";
import { ColumnFilterItem, ColumnProps } from "antd/lib/table/interface";
import {
    SignStatus,
    SignType,
    StuSignIn,
    StuSignInMap,
    StuSignInStatus,
    updateStuSignStatus
} from "service/signIn";
import moment from "moment";
import _ from "underscore";
import { errorMessage, toNumber } from "service/service";
import { updateStuOnLineSignStatus } from "../../../../../service/signIn";
import classNames from "classnames";

interface SignInTableProps {
    signInId: number;
    signStatus: SignStatus;
    data: StuSignIn[];
    onChange: () => void;
    type?: SignType;
}

export default function SignInTable({
    signInId,
    signStatus,
    data,
    onChange,
    type
}: SignInTableProps) {
    // 过滤签到状态
    const filters: ColumnFilterItem[] = useMemo(() => {
        return _.sortBy(
            _.uniq(_.pluck(data, "signStatus")).map(value => ({
                text: StuSignInMap[value],
                value: String(value)
            })),
            "value"
        );
    }, [data]);

    const columns = useMemo<ColumnProps<StuSignIn>[]>(() => {
        const numberObj: ColumnProps<StuSignIn>[] = [
            {
                title: "学生信息",
                dataIndex: "name",
                render: renderStudent
            },
            {
                title: "签到状态",
                dataIndex: "signStatus",
                render: renderStatus,
                align: "center",
                width: 120,
                filters: signStatus === SignStatus.Done ? filters : undefined,
                onFilter: (
                    value: string | number | null | undefined,
                    record: { signStatus: number }
                ) => {
                    return toNumber(value) === record.signStatus;
                }
            }
        ];
        if (type === SignType.Number && signStatus === SignStatus.InProgress) {
            return [...numberObj];
        } else {
            return [
                {
                    title: "序号",
                    render: (text, item, index) => index + 1,
                    align: "center",
                    width: 100,
                    key: "index"
                },
                {
                    title: "班级",
                    dataIndex: "className",
                    align: "center",
                    width: 300
                },
                ...numberObj,
                {
                    title: "签到时间",
                    dataIndex: "signInTime",
                    align: "center",
                    width: 200,
                    render: (text: string) =>
                        text ? moment(text).format("MM-DD HH:mm:ss") : "",
                    sorter:
                        signStatus === SignStatus.Done
                            ? (itemA, itemB) =>
                                  itemA.signInTime > itemB.signInTime ? 1 : -1
                            : undefined,
                    sortDirections: ["ascend", "descend"]
                }
            ];
        }
    }, [type]);

    // 学生信息
    function renderStudent(text: string, item: StuSignIn) {
        return (
            <div className={classes.student}>
                {text}
                <span className={classes.stuNo}>{item.studentno}</span>
            </div>
        );
    }

    // 修改学生签到状态
    async function handleStatusChange(
        item: StuSignIn,
        signStatus: StuSignInStatus
    ) {
        try {
            if (type === SignType.Live) {
                await updateStuOnLineSignStatus({
                    signInId,
                    studentno: item.studentno,
                    signStatus
                });
            } else {
                await updateStuSignStatus({
                    signInId,
                    studentno: item.studentno,
                    signStatus
                });
            }
            onChange();
        } catch (e) {
            message.error(errorMessage(e));
        }
    }

    // 签到状态
    function renderStatus(text: StuSignInStatus, item: StuSignIn) {
        if (signStatus !== SignStatus.Done) {
            return (
                <span
                    className={classNames(
                        classes[`state${text}`],
                        classes.coo
                    )}>
                    {StuSignInMap[text]}
                </span>
            );
        }

        return (
            <Select
                className={classes.select}
                defaultValue={text}
                onChange={(newStatus: number) =>
                    handleStatusChange(item, newStatus)
                }>
                {_.map(StuSignInMap, (label, value) => (
                    <Select.Option key={value} value={toNumber(value)}>
                        {label}
                    </Select.Option>
                ))}
            </Select>
        );
    }

    return (
        <div className={classes.root}>
            <Table
                scroll={{ y: "calc(100vh - 460px)" }}
                columns={columns}
                dataSource={data}
                rowKey="studentno"
                pagination={false}
            />
        </div>
    );
}
